












import { Component, Vue, Watch, Prop } from 'vue-property-decorator';
import CustomFileSystemProvider from 'devextreme/file_management/custom_provider';
import DocumentsService from '@/modules/documents/DocumentsService';
import { DxFileManager, DxPermissions } from 'devextreme-vue/file-manager';
import { updateLocale } from 'moment';
import { EventLogsObjectType } from '@/modules/shared/eventlogs/enums';

import notificationService from '@/services/notificationService';

import PdfViewModal from '@/modules/shared/documents/pdfViewModal.vue';
import ImgViewModal from '@/modules/shared/documents/imgViewModal.vue';
import AuthenticationService from '@/auth/AuthenticationService';
import { SecurityConfig } from '@/installationConfig';

@Component({
  components: {
    DxFileManager,
    DxPermissions,
    PdfViewModal,
    ImgViewModal,
  },
})
export default class FileManager extends Vue {
  @Prop({ required: true })
  public objectId!: string;

  @Prop({ required: true })
  public objectType!: EventLogsObjectType;

  @Prop({ required: false })
  public folderName!: string;

  @Prop({ required: false })
  sharepointUrl!: string;

  private loading: boolean = false;
  private guidList: any[] = []; // Used to manage chunk uploads
  private authService = new AuthenticationService();

  private provider = new CustomFileSystemProvider({
    getItems: async (pathInfo) => {
      const self = this;
      return (
        await DocumentsService.lookup({
          objectId: self.objectId,
          objectType: self.objectType,
          path: pathInfo ? pathInfo.path : '',
          folderName: self.folderName,
        })
      ).data;
    },
    createDirectory: async (options, name) => {
      const self = this;
      return (
        await DocumentsService.createDirectory({
          objectId: self.objectId,
          objectType: self.objectType,
          path: options ? options.path : '',
          name,
        })
      ).data;
    },
    uploadFileChunk: async (file, uploadInfo, destinationDirectory) => {
      /*
                When chunks are uploaded to the server, you receive a GUID.
                This guid should be stored & passed to the server,
                so the server can determinate from which file the chunk is
            */
      const self = this;

      // Get guid item
      const guidItem = self.guidList.find((o) => o.name === file.name);

      // Create uploadData
      const data = {
        index: uploadInfo.chunkIndex,
        totalCount: uploadInfo.chunkCount,
        fileSize: file.size,
        fileName: file.name,
        fileType: file.type,
        fileGuid: guidItem ? guidItem.guid : '',
      };

      // Upload Chunk
      const res = (await DocumentsService.uploadFileChunk(self.objectId, self.objectType, destinationDirectory.path, uploadInfo.chunkBlob, data)).data;

      // Manage guid list
      const guidIndex = self.guidList.findIndex((o) => o.guid === res.guid);
      if (res.completed && guidIndex !== -1) {
        self.guidList.splice(guidIndex, 1);
      }
      if (!res.completed && guidIndex === -1) {
        self.guidList.push({ guid: res.guid, name: file.name });
      }

      return null;
    },
    copyItem: async (item, destinationFolder) => {
      await DocumentsService.copyItem({
        itemKey: item.key,
        destinationFolderKey: destinationFolder.key,
      });

      return null;
    },
    moveItem: async (item, destinationFolder) => {
      await DocumentsService.moveItem({
        itemKey: item.key,
        destinationFolderKey: destinationFolder.key,
      });

      return null;
    },
    deleteItem: async (item) => {
      await DocumentsService.deleteItem(item.key);

      return null;
    },
    renameItem: async (item, name) => {
      await DocumentsService.renameItem({
        itemKey: item.key,
        name,
      });

      return null;
    },
    downloadItems: async (items) => {
      const self = this;
      items.forEach(async (item) => {
        const result = await DocumentsService.downloadItem(item.key);
        const url = window.URL.createObjectURL(result.data);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', result.headers.filename);
        link.click();
      });

      return null;
    },
  });

  get config() {
    const self = this;
    return {
      contextMenu: {
        items: [
          {
            text: 'Bekijken', // custom item with sub items
            onClick: async () => {
              self.loading = true;
              try {
                const selection = (this.$refs.FileManager as DxFileManager).instance!.getSelectedItems();
                if (selection.length > 0) {
                  const result = await DocumentsService.downloadItem(selection[0].key);
                  const url = window.URL.createObjectURL(result.data);

                  const extension = selection[0].name.split('.').pop() as string;
                  const pdfExtensions = [
                    'csv',
                    'doc',
                    'docx',
                    'odp',
                    'ods',
                    'odt',
                    'pot',
                    'potm',
                    'potx',
                    'pps',
                    'ppsx',
                    'ppsxm',
                    'ppt',
                    'pptm',
                    'pptx',
                    'rtf',
                    'xls',
                    'xlsx',
                    'pdf',
                  ];
                  const imgExtesions = ['png', 'jpg', 'jpeg', 'giff', 'svg', 'gif'];

                  if (pdfExtensions.indexOf(extension.toLowerCase()) !== -1) {
                    (this.$refs.PdfViewModal as PdfViewModal).show(url);
                    return;
                  }

                  if (imgExtesions.indexOf(extension.toLowerCase()) !== -1) {
                    (this.$refs.ImgViewModal as ImgViewModal).show(url);
                    return;
                  }

                  notificationService.showErrorMessage('Voorbeeld wordt niet ondersteund voor dit bestand!');
                }
              } catch (err) {
                notificationService.showErrorMessage('Er is iets verkeerd gelopen');
              } finally {
                self.loading = false;
              }
            },
            icon: 'fas fa-eye',
          },
          'create',
          'upload',
          'rename',
          'move',
          'copy',
          'delete',
          'refresh',
          'download',
        ],
      },
      toolbar: {
        items: [
          'showNavPane',
          'create',
          'upload',
          {
            name: this.$t('buttons.OpenSharepoint'),
            widget: 'dxButton',
            visible: self.sharepointUrl!! && this.authService.isNotInRole([SecurityConfig.qualityGuardOneSkinGroup || '']),
            options: {
              text: this.$t('buttons.OpenSharepoint'),
              icon: 'fas fa-external-link-alt',
              onClick: async () => {
                window.open(self.sharepointUrl);
              },
            },
          },
          'switchView',
          { name: 'separator', location: 'after' },
          'refresh',
        ],
      },
    };
  }

  get rootFolderName() {
    return 'Documenten';
    switch (this.objectType) {
      case EventLogsObjectType.MachineryRental:
        return 'Verhuurcontract';
      default:
        return 'Project';
    }
  }
}
